﻿import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Button, Alert, InputGroup, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { useAuth } from '@/context/AuthContext';
import { config } from '@/config/env';

export default function LoginPage() {
  const { t } = useTranslation('auth');
  const { t: tc } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [showForgot, setShowForgot] = useState(false);
  const [forgotUsername, setForgotUsername] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState('');
  const [forgotSuccess, setForgotSuccess] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!username.trim()) { setError(t('invalidUsername')); return; }
    if (!password.trim()) { setError(t('invalidPassword')); return; }
    setLoading(true);
    try {
      const url = config.WSREST_TABATA.replace(/\/+$/, '') + '/login/' + config.SITE + '/' + config.NOME_APP;
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      if (res.status === 401) { setError('Accesso non autorizzato. Verifica username e password.'); return; }
      if (!res.ok) { setError('Errore del server (' + res.status + '). Riprova.'); return; }
      const data = await res.json();
      if (!data || !data.t) { setError('Accesso non autorizzato.'); return; }

      // Password scaduta → salva token temporaneo e redirect
      if (data.password_expired === true) {
        sessionStorage.setItem('tempToken', data.t);
        sessionStorage.setItem('tempTokenExp', data.tex);
        navigate('/password-scaduta');
        return;
      }

      login(data);
      navigate('/visite/nuova');
    } catch (err: any) {
      if (err.message?.includes('Failed to fetch')) {
        setError('Errore di connessione. Verifica la tua connessione internet.');
      } else {
        setError(t('loginError'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotOpen = () => {
    setForgotUsername(username);
    setForgotError('');
    setForgotSuccess('');
    setShowForgot(true);
  };

  const handleForgotSubmit = async () => {
    setForgotError('');
    setForgotSuccess('');
    if (!forgotUsername.trim()) { setForgotError(t('invalidUsername')); return; }
    setForgotLoading(true);
    try {
      const url = config.WSREST_TABATA.replace(/\/+$/, '') + '/reset-password';
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: forgotUsername }),
      });
      if (!res.ok) {
        let errMsg = t('forgotPasswordError');
        try {
          const text = await res.text();
          if (text) {
            const errData = JSON.parse(text);
            if (errData?.error) errMsg = errData.error;
          }
        } catch { /* ignore parse error */ }
        setForgotError(errMsg);
        return;
      }
      // Successo — mostra messaggio anche se body è vuoto
      setForgotSuccess(t('forgotPasswordSuccess'));
    } catch (err: any) {
      // Errore di rete
      if (err.message?.includes('Failed to fetch') || err.message?.includes('NetworkError')) {
        setForgotError(t('connectionError') || 'Errore di connessione. Riprova.');
      } else {
        setForgotError(t('forgotPasswordError'));
      }
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="d-lg-flex min-vh-100">
      <div className="d-flex flex-column py-4" style={{ width: '100%', maxWidth: 520, minWidth: 320, paddingLeft: 40, paddingRight: 40 }}>
        <div className="d-flex flex-column justify-content-center flex-grow-1">
          <div className="mb-4 pb-2">
            <img src="/images/logo.png" alt="LMeet" style={{ height: 56 }} />
          </div>
          <h1 className="h2 mb-4">{t('welcome')}</h1>
          <Form onSubmit={handleSubmit} noValidate>
            <Form.Group className="mb-3">
              <Form.Control size="lg" type="text" placeholder={t('username')}
                value={username} onChange={e => { setUsername(e.target.value); setError(''); }}
                autoComplete="off" autoCorrect="off" autoCapitalize="none" />
            </Form.Group>
            <Form.Group className="mb-3">
              <InputGroup>
                <Form.Control size="lg" type={showPassword ? 'text' : 'password'} placeholder={t('password')}
                  value={password} onChange={e => { setPassword(e.target.value); setError(''); }} autoComplete="off" />
                <Button variant="outline-secondary" onClick={() => setShowPassword(!showPassword)} tabIndex={-1}>
                  {showPassword ? <FiEyeOff /> : <FiEye />}
                </Button>
              </InputGroup>
            </Form.Group>
            <div className="mb-4">
              <a href="#" className="text-decoration-none" style={{ fontSize: 13 }}
                onClick={e => { e.preventDefault(); handleForgotOpen(); }}>
                {t('forgotPassword')}
              </a>
            </div>
            {error && <Alert variant="danger" className="py-2" style={{ fontSize: 13 }}>{error}</Alert>}
            <Button type="submit" className="w-100 login-btn btn-lg" disabled={loading}>
              {loading && <span className="spinner-border spinner-border-sm me-2" />}
              {t('enter')}
            </Button>
          </Form>
        </div>
        <div className="pt-4">
          <small className="text-muted">
            &copy; {new Date().getFullYear()} Partner Tecnologico{' '}
            <a href="https://www.tabatatech.it" target="_blank" rel="noreferrer">TABATA</a>
          </small>
        </div>
      </div>
      <div className="d-none d-lg-block flex-grow-1 position-relative" style={{ minHeight: '100vh' }}>
        <img src="/images/banner_login.png" alt="" className="position-absolute top-0 start-0 w-100 h-100" style={{ objectFit: 'cover' }} />
      </div>

      <Modal show={showForgot} onHide={() => setShowForgot(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 18 }}>{t('forgotPasswordTitle')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted" style={{ fontSize: 13 }}>{t('forgotPasswordDesc')}</p>
          <Form.Group className="mb-3">
            <Form.Control type="text" placeholder={t('username')} value={forgotUsername}
              onChange={e => { setForgotUsername(e.target.value); setForgotError(''); setForgotSuccess(''); }} autoComplete="off" />
          </Form.Group>
          {forgotError && <Alert variant="danger" className="py-2" style={{ fontSize: 13 }}>{forgotError}</Alert>}
          {forgotSuccess && <Alert variant="success" className="py-2" style={{ fontSize: 13 }}>{forgotSuccess}</Alert>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowForgot(false)}>{tc('actions.cancel')}</Button>
          <Button variant="primary" size="sm" onClick={handleForgotSubmit} disabled={forgotLoading || !!forgotSuccess}>
            {forgotLoading && <span className="spinner-border spinner-border-sm me-2" />}
            {t('send')}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
